import React, { useState, useEffect, useRef } from "react";
import './TimerComponent.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus,faBell, faEdit, faWindowRestore, faTimes, faPlay, faPause, faRedo } from '@fortawesome/free-solid-svg-icons';
import notificationSound from '../..//assets/sounds/notification-sound.mp3';

function TimerComponent() {
  const [timers, setTimers] = useState(() => {
    const savedTimers = localStorage.getItem('timers');
    return savedTimers ? JSON.parse(savedTimers) : [
      { id: 1, name: "5 min", time: 300, remainingTime: 300, isRunning: false },
      { id: 2, name: "10 min", time: 600, remainingTime: 600, isRunning: false },
      { id: 3, name: "30 min", time: 1800, remainingTime: 1800, isRunning: false },
      { id: 4, name: "1 hour", time: 3600, remainingTime: 3600, isRunning: false },
    ];
  });

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingTimer, setEditingTimer] = useState(null);
  const [timerName, setTimerName] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");
  const [floatingTimer, setFloatingTimer] = useState(() => {
    const savedFloatingTimer = localStorage.getItem('floatingTimer');
    return savedFloatingTimer ? JSON.parse(savedFloatingTimer) : null;
  });
  const [notification, setNotification] = useState(null);
  const [isTimerRinging, setIsTimerRinging] = useState(false);
  const [snoozeOptionsVisible, setSnoozeOptionsVisible] = useState(false);

  const floatingRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const audioRef = useRef(null);
  const snoozeTimeout = useRef(null);

  useEffect(() => {
    localStorage.setItem('timers', JSON.stringify(timers));
  }, [timers]);

  useEffect(() => {
    localStorage.setItem('floatingTimer', JSON.stringify(floatingTimer));
  }, [floatingTimer]);

  const startTimer = (id) => {
    setTimers((prevTimers) =>
      prevTimers.map((timer) =>
        timer.id === id ? { ...timer, isRunning: !timer.isRunning } : timer
      )
    );
  };

  const resetTimer = (id) => {
    setTimers((prevTimers) =>
      prevTimers.map((timer) =>
        timer.id === id ? { ...timer, remainingTime: timer.time, isRunning: false } : timer
      )
    );
  };

  const deleteTimer = (id) => {
    setTimers((prevTimers) => prevTimers.filter((timer) => timer.id !== id));
  };

  const addOrUpdateTimer = () => {
    const totalTimeInSeconds =
      parseInt(hours || 0) * 3600 +
      parseInt(minutes || 0) * 60 +
      parseInt(seconds || 0);

    if (editingTimer) {
      setTimers((prevTimers) =>
        prevTimers.map((timer) =>
          timer.id === editingTimer.id
            ? {
                ...timer,
                name: timerName,
                time: totalTimeInSeconds,
                remainingTime: totalTimeInSeconds,
              }
            : timer
        )
      );
    } else {
      const newTimer = {
        id: Date.now(),
        name: timerName,
        time: totalTimeInSeconds,
        remainingTime: totalTimeInSeconds,
        isRunning: false,
      };
      setTimers([...timers, newTimer]);
    }

    closePopup();
  };

  const openPopup = (timer = null) => {
    if (timer) {
      setEditingTimer(timer);
      setTimerName(timer.name);
      setHours(Math.floor(timer.time / 3600));
      setMinutes(Math.floor((timer.time % 3600) / 60));
      setSeconds(timer.time % 60);
    } else {
      setEditingTimer(null);
      setTimerName("");
      setHours("");
      setMinutes("");
      setSeconds("");
    }
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setEditingTimer(null);
  };

  const expandTimer = (timer) => {
    setFloatingTimer(timer);
    if (floatingRef.current) {
      const floatingElem = floatingRef.current;
      floatingElem.style.left = `calc(50% - ${floatingElem.offsetWidth / 2}px)`;
      floatingElem.style.top = `calc(50% - ${floatingElem.offsetHeight / 2}px)`;
    }
  };

  const toggleFloatingTimer = () => {
    if (floatingTimer) {
      setTimers((prevTimers) =>
        prevTimers.map((timer) =>
          timer.id === floatingTimer.id ? { ...timer, isRunning: !timer.isRunning } : timer
        )
      );
    }
  };

  const closeFloatingTimer = () => {
    setFloatingTimer(null);
  };

  const handleTimerCompletion = (timer) => {
    setIsTimerRinging(true);
    setFloatingTimer(null);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => console.error('Audio playback failed:', error));
    }
    setNotification(timer);
  };

  const stopTimer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsTimerRinging(false);
    setNotification(null);
    setSnoozeOptionsVisible(false);
    clearTimeout(snoozeTimeout.current);
  };

  const snoozeTimer = (minutes) => {
    stopTimer();
    const snoozeTime = minutes * 60 * 1000;
    snoozeTimeout.current = setTimeout(() => {
      handleTimerCompletion(notification);
    }, snoozeTime);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prevTimers) =>
        prevTimers.map((timer) => {
          if (timer.isRunning && timer.remainingTime > 0) {
            return { ...timer, remainingTime: timer.remainingTime - 1 };
          } else if (timer.isRunning && timer.remainingTime === 0) {
            handleTimerCompletion(timer);
            return { ...timer, isRunning: false };
          }
          return timer;
        })
      );

      if (floatingTimer) {
        const updatedTimer = timers.find(timer => timer.id === floatingTimer.id);
        if (updatedTimer) {
          setFloatingTimer(updatedTimer);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timers, floatingTimer]);

  const handleDragStartFloating = (e) => {
    dragOffset.current = {
      x: e.clientX - floatingRef.current.getBoundingClientRect().left,
      y: e.clientY - floatingRef.current.getBoundingClientRect().top,
    };
    document.addEventListener("mousemove", handleDragFloating);
    document.addEventListener("mouseup", handleDragEndFloating);
  };

  const handleDragFloating = (e) => {
    if (floatingRef.current) {
      const floatingElem = floatingRef.current;
      floatingElem.style.left = `${e.clientX - dragOffset.current.x}px`;
      floatingElem.style.top = `${e.clientY - dragOffset.current.y}px`;
    }
  };

  const handleDragEndFloating = () => {
    document.removeEventListener("mousemove", handleDragFloating);
    document.removeEventListener("mouseup", handleDragEndFloating);
  };

  return (
    <div className="timer-container">
      {timers.map((timer, index) => (
        <div
          key={timer.id}
          className="timer-card"
        >
          <div className="timer-header">
            <span>{timer.name}</span>
            <div className="timer-controls">
              <button onClick={() => openPopup(timer)} className="control-button">
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button onClick={() => deleteTimer(timer.id)} className="control-button">
                <FontAwesomeIcon icon={faTrash} />
              </button>
              <button onClick={() => expandTimer(timer)} className="control-button">
                <FontAwesomeIcon icon={faWindowRestore} />
              </button>
            </div>
          </div>
          <div className={`timer-display ${timer.isRunning ? 'running' : ''}`}>
            {new Date(timer.remainingTime * 1000).toISOString().substr(11, 8)}
            <div className="circle-controls">
              <button onClick={() => startTimer(timer.id)} className="circle-control-button">
                {timer.isRunning ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faPlay} />}
              </button>
              <button onClick={() => resetTimer(timer.id)} className="circle-control-button">
                <FontAwesomeIcon icon={faRedo} />
              </button>
            </div>
          </div>
        </div>
      ))}

      {floatingTimer && (
        <div
          ref={floatingRef}
          className="floating-timer"
          onMouseDown={handleDragStartFloating}
        >
          <div className="floating-timer-content">
            <button onClick={closeFloatingTimer} className="close-floating">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="floating-display">
              {new Date(floatingTimer.remainingTime * 1000).toISOString().substr(11, 8)}
            </div>
            <button onClick={toggleFloatingTimer} className="control-button">
              {floatingTimer.isRunning ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faPlay} />}
            </button>
          </div>
        </div>
      )}

      <button className="floating-button" onClick={() => openPopup()}>
        <FontAwesomeIcon icon={faPlus} />
      </button>

      {isPopupOpen && (
        <div className="popup">
          <div className="popup-content">
            <h3>{editingTimer ? "Edit Timer" : "Add Timer"}</h3>
            <input
              type="text"
              placeholder="Timer Name"
              value={timerName}
              onChange={(e) => setTimerName(e.target.value)}
            />
            <div className="time-inputs">
              <input
                type="number"
                placeholder="Hours"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min="0"
              />
              <input
                type="number"
                placeholder="Minutes"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                min="0"
              />
              <input
                type="number"
                placeholder="Seconds"
                value={seconds}
                onChange={(e) => setSeconds(e.target.value)}
                min="0"
              />
            </div>
            <div className="popup-actions">
              <button onClick={addOrUpdateTimer}>
                {editingTimer ? "Update" : "Add"}
              </button>
              <button onClick={closePopup}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {isTimerRinging && notification && (
        <div className="timer-notification">
          <div className="notification-content">
            <FontAwesomeIcon icon={faBell} className="bell-icon" />
            <p>{notification.name} is done!</p>
            <div className="notification-actions">
              <button className="dismiss-button" onClick={stopTimer}>Dismiss</button>
              <button className="snooze-button" onClick={() => setSnoozeOptionsVisible(!snoozeOptionsVisible)}>Snooze</button>
            </div>
            {snoozeOptionsVisible && (
              <div className="snooze-options">
                <button onClick={() => snoozeTimer(5)}>5 minutes</button>
                <button onClick={() => snoozeTimer(10)}>10 minutes</button>
                <button onClick={() => snoozeTimer(15)}>15 minutes</button>
                <button onClick={() => snoozeTimer(30)}>30 minutes</button>
              </div>
            )}
          </div>
        </div>
      )}

      <audio ref={audioRef}>
        <source src={notificationSound} type="audio/mp3" />
      </audio>
    </div>
  );
}

export default TimerComponent;
